import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { ThemingService } from './shared/services/theming.service';
import { ClubService } from './shared/services/club.service';
import { ConfigurationService } from './shared/configuration/configuration.service';
import { NotificationsService } from './shared/services/notifications.service';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, first, Subject, takeUntil } from 'rxjs';
import posthog from 'posthog-js';
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { PushNotificationService } from './shared/services/push-notification.service';
import { LoaderService } from './shared/services/loader.service';
import { ApplicationRef, OnDestroy } from '@angular/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Booking';
  isOnline = true;
  private destroy$ = new Subject<void>();

  get isServiceDown(): boolean {
    return this.clubsService.serviceDown();
  }

  constructor(
    private themingService: ThemingService,
    private clubsService: ClubService,
    private configService: ConfigurationService,
    private notificationService: NotificationsService,
    private pushNotificationService: PushNotificationService,
    private loaderService: LoaderService,
    private renderer: Renderer2,
    private router: Router,
    private appRef: ApplicationRef,
    @Inject(DOCUMENT) private document: Document,
    private swUpdate: SwUpdate
  ) {
    this.clearStaleCache();
    this.setupNetworkListeners();
    this.setupRouteListeners();
    this.setupUpdateListeners();
    this.setupSwRecovery();

    // ThemingService applies the saved/system theme in its own constructor
    // (instantiated via DI above), so no explicit boot call is needed.
    this.pushNotificationService.init();
  }

  private async clearStaleCache() {
    const currentHash = environment.gitHash;
    const storedHash = localStorage.getItem('app_git_hash');

    if (storedHash && storedHash !== currentHash) {
      console.log(`App updated: ${storedHash} -> ${currentHash}. Clearing SW caches.`);

      // Clear all Service Worker caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(name => {
            console.log(`Deleting cache: ${name}`);
            return caches.delete(name);
          })
        );
      }

      // Unregister and re-register the app's Service Worker. Never the
      // Firebase messaging worker: unregistering it ends the browser's push
      // subscription, which used to switch push off for everyone on every
      // release.
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          const script =
            reg.active?.scriptURL ?? reg.waiting?.scriptURL ?? reg.installing?.scriptURL ?? '';
          if (script.includes('firebase-messaging-sw')) continue;
          await reg.unregister();
          console.log('Service Worker unregistered');
        }
      }

      localStorage.setItem('app_git_hash', currentHash);

      // Reload to get fresh SW and assets
      window.location.reload();
      return;
    }

    if (!storedHash) {
      localStorage.setItem('app_git_hash', currentHash);
    }
  }

  private setupUpdateListeners() {
    if (this.swUpdate.isEnabled) {
      // 1. Check when the app is stable (initial load)
      const appIsStable$ = this.appRef.isStable.pipe(
        first((isStable) => isStable === true)
      );

      appIsStable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.swUpdate.checkForUpdate();
      });

      // 2. Check whenever the user navigates to a new page (active usage)
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.swUpdate.checkForUpdate();
        });

      // 3. Check when the user switches back to the tab
      this.document.addEventListener('visibilitychange', () => {
        if (this.document.visibilityState === 'visible') {
          this.swUpdate.checkForUpdate();
        }
      });

      // 4. Handle version ready
      this.swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          posthog.capture('Update available');
          this.swUpdate.activateUpdate().then(() => {
            document.location.reload();
          });
        });
    }
  }

  private async setupSwRecovery() {
    if (!('serviceWorker' in navigator)) return;

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;

      // If SW is in 'redundant' state, it's broken — unregister and reload
      if (registration.active?.state === 'redundant') {
        console.warn('SW is redundant. Unregistering and reloading...');
        await registration.unregister();
        window.location.reload();
        return;
      }

      // Handle UNRECOVERABLE_STATE from Angular SW
      if (this.swUpdate.isEnabled) {
        this.swUpdate.unrecoverable
          .pipe(takeUntil(this.destroy$))
          .subscribe((event) => {
            console.error('SW unrecoverable state:', event.reason);
            posthog.capture('SW unrecoverable', { reason: event.reason });

            // Nuclear: clear everything and reload
            caches.keys().then(names =>
              Promise.all(names.map(name => caches.delete(name)))
            ).then(() => {
              registration.unregister().then(() => {
                window.location.reload();
              });
            });
          });
      }
    } catch (err) {
      console.error('SW recovery check failed:', err);
    }
  }

  private setupNetworkListeners() {
    this.isOnline = navigator.onLine;
    window.addEventListener('online', () => (this.isOnline = true));
    window.addEventListener('offline', () => (this.isOnline = false));
  }

  private setupRouteListeners() {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loaderService.show();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        // Small delay to ensure smooth transition
        setTimeout(() => this.loaderService.hide(), 300);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async ngOnInit() {
    const relative_path = await this.clubsService.getLogoRelativePath();
    const test = environment;
    let logoUrl: HTMLLinkElement | null = this.document.querySelector(
      'link[rel*="icon"]'
    ) as HTMLLinkElement;
    this.renderer.setAttribute(logoUrl, 'href', relative_path);
  }

  /** Actively suspended this club for an unpaid bill: the app shows only the block screen. */
  get isSuspended(): boolean {
    return this.clubsService.flags()?.billing_suspended === true;
  }

  retryConnection() {
    document.location.reload();
  }
}
